import { DEFAULT_LANG } from '@gifsa/language-utils/lang'
import {
  COUNTRY,
  MONEY_TRANSFER,
  IMT_LIVE,
  CURRENCIES,
  TOPUP,
  REVIEW,
} from '@gifsa/language-utils/routes'

/**
 * Adds a directory separator between all path components.
 * The resulting path is sanitized so their is always one directory separator character
 * between each path components.
 *
 * Note: The leading and trailing slash of the resulting path are not automatically included
 * @param parts - list of url parts
 * @example
 * join('a', '/b', 'c/') => 'a/b/c/'
 * join('/a', '', 'c') => '/a/c'
 *
 */
export function join(...components: Array<string | undefined | null>) {
  return components
    .filter(Boolean)
    .join('/')
    .replace(/(:\/\/)|(\/)+/g, '$1$2')
}

// URL.canParse is not yet supported on all platforms
function canParse(url: string) {
  try {
    return Boolean(new URL(url))
  } catch {
    return false
  }
}

export function addAffiliateMobileAppParams(url?: string): string | undefined {
  const { redirectUrl } = useRuntimeConfig().public
  if (!url || !canParse(url) || !url.startsWith(redirectUrl)) {
    return url
  }

  const link = new URL(url)
  const params = link.searchParams

  const { $lang, $cookie } = useNuxtApp()
  if (!params.get('locale')) {
    params.set('lng', $lang)
  }

  if (__IS_MOBILE_APP__) {
    params.set('uid', $cookie.value.getId() || '')
    params.set('abg', $cookie.value.getAbGroup() || '')
    params.set('app', 'mobile')
  }
  return link.href
}

export function getHomeUrl(lang?: string) {
  const lng = lang || useNuxtApp().$lang
  const path = lng === DEFAULT_LANG ? '/' : `/${lng}/`
  const { siteUrl } = useRuntimeConfig().public
  return join(siteUrl, path)
}

export function getCountryUrl(lang?: string, countrySlug?: string) {
  const lng = lang || useNuxtApp().$lang
  const { siteUrl } = useRuntimeConfig().public
  const route = COUNTRY.find((r) => r.lang === lng)
  return join(siteUrl, route?.url, countrySlug).toLowerCase()
}

export function getTransferUrl(
  corridor: {
    countryFrom: { slug: string }
    countryTo: { slug: string }
    currencyFrom?: { iso: string }
    currencyTo?: { iso: string }
    amount?: number | null | ''
  },
  lang?: string,
) {
  const lng = lang || useNuxtApp().$lang
  const { siteUrl } = useRuntimeConfig().public
  const route = MONEY_TRANSFER.find((r) => r.lang === lng)
  return join(
    siteUrl,
    route?.url,
    corridor.countryFrom.slug,
    corridor.countryTo.slug,
    corridor.currencyFrom?.iso,
    corridor.currencyTo?.iso,
    corridor.amount ? String(corridor.amount) : undefined, // this makes 0 not allowed too.
  ).toLowerCase()
}

export function getIMTLiveUrl(
  corridor: {
    countryFrom: { iso: string }
    countryTo: { iso: string }
    currencyFrom: { iso: string }
    currencyTo: { iso: string }
    amount?: number
    payout?: string
  },
  lang?: string,
) {
  const lng = lang || useNuxtApp().$lang
  const { siteUrl } = useRuntimeConfig().public
  const route = IMT_LIVE.find((r) => r.lang === lng)
  return join(
    siteUrl,
    route?.url,
    corridor.countryFrom.iso,
    corridor.countryTo.iso,
    corridor.currencyFrom.iso,
    corridor.currencyTo.iso,
    corridor.amount ? String(corridor.amount) : undefined,
    corridor.payout ? `?tab=${corridor.payout}` : undefined,
  ).toLowerCase()
}

export function getCurrencyUrl(
  corridor: { currencyFrom: { iso: string }; currencyTo: { iso: string } },
  lang?: string,
): string {
  const lng = lang || useNuxtApp().$lang
  const { siteUrl } = useRuntimeConfig().public
  const route = CURRENCIES.find((r) => r.lang === lng)
  return join(siteUrl, route?.url, corridor.currencyFrom.iso, corridor.currencyTo.iso).toLowerCase()
}

export function getTopupUrl(lang?: string, countrySlug?: string, operatorSlug?: string): string {
  const lng = lang || useNuxtApp().$lang
  const { siteUrl } = useRuntimeConfig().public
  const route = TOPUP.find((r) => r.lang === lng)
  return join(siteUrl, route?.url, countrySlug, operatorSlug).toLowerCase()
}

export function getReviewUrl(providerSlug: string, lang?: string): string {
  const lng = lang || useNuxtApp().$lang
  const { siteUrl } = useRuntimeConfig().public
  const route = REVIEW.find((r) => r.lang === lng)
  return join(siteUrl, route?.url, providerSlug).toLowerCase()
}
